import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { ConfirmationModal } from "./ConfirmationModal";
import { iconStatusMap, statusToGroup } from "./DefaultImports";

const DropdownTrigger = styled.div`
  width: 100%;
  min-height: 30px;
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: ${({ color }) => color || "#99b0ad"};
  font-weight: 300;
  font-family: "Arial Black", "Segoe UI", sans-serif;
  cursor: pointer;
  padding: 0px 4px;
  box-sizing: border-box;
  overflow: hidden;
`;

const TextWrapper = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 300;
  font-family: "Arial Black", "Segoe UI", sans-serif;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
`;

const WaitingListNumber = styled.span`
  flex: 0 0 auto;
  font-size: clamp(12px, 6.5cqw, 15px);
  line-height: 1.2;
  color: inherit;
`;

const LabelBox = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  container-type: inline-size;
`;

const TextLabel = styled.span`
  font-size: clamp(12px, 6cqw, 15px);
  max-width: 100%;
  white-space: normal;
  overflow: hidden;
  display: block;
  line-height: 1.25;

  && {
    color: ${({ $color }) => $color || "inherit"} !important;
  }

  && * {
    color: inherit !important;
  }

  strong {
    font-weight: 900;
    font-size: clamp(12px, 6.5cqw, 15px);
    color: inherit !important;
  }
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  flex: 0 0 auto;
  color: inherit;
  padding-right: 10px;
`;

const PortalDropdown = styled.div`
  position: absolute;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  min-width: 180px;
  z-index: 3000;
  padding: 4px 0;
  max-height: calc(100vh - 16px);
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  background-color: ${({ active }) => (active ? "#ee754c" : "white")};
  color: ${({ active, statusColor }) =>
    active ? "white" : statusColor || "#99b0ad"};
  font-weight: ${({ active }) => (active ? "bold" : "normal")};

  &:hover {
    background-color: ${({ active }) => (active ? "#ee754c" : "#f0f0f0")};
  }
`;

const StatusDropdown = ({
  reservation,
  allowedStatuses,
  onSelectStatus,
  getStatusColor,
  getStatusLabel,
}) => {
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const getGroupedStatus = (status) => {
    return statusToGroup?.[status] || status;
  };

  const getGroupedStatusColor = (status) => {
    const group = getGroupedStatus(status);
    const iconColor = iconStatusMap?.[group]?.color?.trim();

    if (iconColor) return iconColor;

    if (typeof getStatusColor === "function") {
      return getStatusColor(status);
    }

    return "#99b0ad";
  };

  const currentStatusColor = getGroupedStatusColor(reservation.status);

  useLayoutEffect(() => {
    if (visible && wrapperRef.current && dropdownRef.current) {
      const triggerRect = wrapperRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();

      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const margin = 4;

      let top = triggerRect.bottom + window.scrollY;
      let left = triggerRect.left + window.scrollX;

      if (left + dropdownRect.width > viewportW + window.scrollX) {
        left = viewportW - dropdownRect.width - margin;
      }

      if (left < margin) {
        left = margin;
      }

      if (top + dropdownRect.height > viewportH + window.scrollY - margin) {
        top = triggerRect.top - dropdownRect.height + window.scrollY;

        if (top < window.scrollY + margin) {
          top = window.scrollY + margin;
        }
      }

      setCoords({ top, left });
    }
  }, [visible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setVisible(false);
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);

  const handleDropdownItemClick = (status) => {
    if (status !== reservation.status) {
      setPendingStatus(status);
      setShowModal(true);
    }
  };

  const handleConfirm = () => {
    onSelectStatus(pendingStatus);
    setShowModal(false);
    setVisible(false);
    setPendingStatus(null);
  };

  const handleCancel = () => {
    setShowModal(false);
    setPendingStatus(null);
  };

  return (
    <>
      <DropdownTrigger
        ref={wrapperRef}
        onClick={() => setVisible((prev) => !prev)}
        color={currentStatusColor}
      >
        <TextWrapper>
          {reservation.waiting_list_number != null && (
            <WaitingListNumber>
              {reservation.waiting_list_number}º
            </WaitingListNumber>
          )}

          <LabelBox>
            <TextLabel $color={currentStatusColor}>
              {getStatusLabel(reservation.status)}
            </TextLabel>
          </LabelBox>

          <ChevronIcon icon={visible ? faChevronUp : faChevronDown} size="xl" />
        </TextWrapper>
      </DropdownTrigger>

      {visible &&
        createPortal(
          <PortalDropdown
            ref={dropdownRef}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
            }}
          >
            {allowedStatuses.map((status) => {
              const statusColor = getGroupedStatusColor(status);

              return (
                <DropdownItem
                  key={status}
                  active={status === reservation.status}
                  statusColor={statusColor}
                  style={{
                    pointerEvents:
                      status === reservation.status ? "none" : "auto",
                    opacity: status === reservation.status ? 0.6 : 1,
                  }}
                  onClick={() => {
                    handleDropdownItemClick(status);
                    setVisible(false);
                  }}
                >
                  {getStatusLabel(status)}
                </DropdownItem>
              );
            })}
          </PortalDropdown>,
          document.body,
        )}

      <ConfirmationModal
        show={showModal}
        onHide={handleCancel}
        onConfirm={handleConfirm}
        title="Alterar estado"
        body={
          <>
            Tens a certeza que pretendes alterar o estado da reserva para{" "}
            <strong>{getStatusLabel(pendingStatus)}</strong>?
          </>
        }
        confirmLabel="Alterar estado"
        cancelLabel="Cancelar"
        confirmColor="#EE754C"
        cancelColor="#99B0AD"
      />
    </>
  );
};

export default StatusDropdown;
